import { Component, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Payment } from "src/app/models/payment.model";
import { BehaviorSubject, Subscription, first } from "rxjs";
import { AccountService } from "src/app/services";
import { ScrBaseComponent } from "../../base.component";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { AutoRenewConfirmationComponent } from "./autorenew.confirmation.component";
import { BillingInformationComponent } from "./billinginformation.component";
import { BillingOrder } from "src/app/models/billingorder.model";
import { PaymentInformationComponent } from "./payment.information.component";
import { pickBy } from 'lodash-es';
import { PaymentService } from "src/app/services/payment.service";
import { NavigationExtras, Router } from "@angular/router";
import { Account } from "src/app/models/account.model";
import { ShortCodesRole } from "../../../models/constants";

export type FormGroupControls = Record<string, FormControl<string | number | null> | FormGroup>;

export interface CheckoutState {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  form?: FormGroup<any>;
  account?: Account;
  submitted?: boolean;
  applyCredit?: boolean;
  credit?: number;
  totalPrice?: number;
  anyAutoRenew?: boolean;
  anyMonthlyBilling?: boolean;
  amountDueAfterCredit?: number;
  creditToBeApplied?: number;
}

@Component({
  templateUrl: './checkout.payment.component.html'
})
export class CheckoutPaymentComponent extends ScrBaseComponent implements OnInit, OnDestroy {

  CognitoGroup: typeof ShortCodesRole = ShortCodesRole;

  public checkoutState = new BehaviorSubject<CheckoutState>({
    submitted: false
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public form!: FormGroup<any>;
  public submitted = false;
  public account?: Account;

  public payment!: Payment;
  public showMonthlyBillingConfirmation = false;
  public paymentDefaults: BillingOrder | undefined;
  private creditToBeApplied = 0;
  private applyCredit = false;
  private subscription?: Subscription;

  @ViewChild(AutoRenewConfirmationComponent, { static: true }) private autoRenewConfirm!: AutoRenewConfirmationComponent;
  @ViewChild(BillingInformationComponent, { static: true }) private billingInfo!: BillingInformationComponent;
  @ViewChild(PaymentInformationComponent, { static: true }) private paymentInfo!: PaymentInformationComponent;

  constructor(private router: Router, private paymentService: PaymentService, private accountService: AccountService) {
    super();
  }

  ngOnInit(): void {
    this.accountService.getAccount(true).pipe(first()).subscribe(rsp => {
      this.account = rsp.account;
      this.showMonthlyBillingConfirmation = (rsp.account.monBillEnabled === true);
      this.checkoutState.next({
        submitted: false,
        form: this.form,
        account: this.account,
        credit: this.account.unappliedCredits
      });
    });

    this.form = new FormGroup(
      Object.assign({},
        {
          confirmAutoRenew: new FormControl(false, []),
          confirmMonthlyBilling: new FormControl(false, []),
          slaAgreement: new FormControl<boolean>(false, [Validators.required]),
          bestPractices: new FormControl<boolean>(false, [Validators.required]),
          poNumber: new FormControl<string>('', [])
        },
        this.billingInfo.formGroup(),
        this.paymentInfo.formGroup()
      )
    );

    if (history.state.payment) {
      // push onto the browser history...
      //history.pushState();
    }
    this.payment = history.state.payment;
    if (this.payment?.orders) {
      this.paymentDefaults = this.payment.orders[0];
      if (this.paymentDefaults && this.paymentDefaults.poNumber) {
        this.form.get('poNumber')?.setValue(this.paymentDefaults.poNumber);
      }
    }

    this.checkoutState.next({
      submitted: false,
      form: this.form,
      account: this.account
    });

    this.subscription = this.checkoutState.subscribe(upd => {

      this.reviewUpdate(upd.anyAutoRenew, upd.anyMonthlyBilling);
      if (upd.credit) {
        this.creditToBeApplied = upd.credit;
      }
      if (upd.creditToBeApplied) {
        this.creditToBeApplied = upd.creditToBeApplied;
      }
      if (upd.applyCredit !== undefined) {
        this.applyCredit = upd.applyCredit;
      }
    });
  }

  // When we are leaving this page
  override ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    super.ngOnDestroy();

  }
  public cancelPayment() {
    this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['billing', { outlets: { 'billing-tab': ['needs-payment'] } }] } }]);
  }

  public completePayment() {

    this.clearMessages();

    this.submitted = true;
    for (const name in this.form.controls) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const ctrl = this.form.get(name) as any;
      if (ctrl['contains']) {
        const fg = ctrl as FormGroup;
        fg.markAllAsTouched();
      }
      else {
        ctrl.markAsTouched();
        ctrl.markAsDirty();
      }
    }

    const valid = this.form.valid;
    if (!valid) {
      //console.debug(JSON.stringify(this.form.errors));
      //console.debug('form not valid');

      /*
      
      if we need to debug what fields are in error, uncomment this.

      Object.keys(this.form.controls).forEach(k => {
        const e = this.form.get(k);
        console.debug(k + ' ' + e?.invalid + ' ' + JSON.stringify(e?.errors));
        if (k === 'ach' || k === 'cc') {
          const childFg = (e as FormGroup);
          Object.keys(childFg.controls).forEach(kk => {
            const e = childFg.get(kk);
            console.debug(k + '.' + kk + ' ' + e?.invalid + ' ' + JSON.stringify(e?.errors));
          });
        }
      });
      */

      this.scrollToFirstError();

      return;
    }
    const err = this.paymentInfo.validateBeforeSubmit();
    if (err) {
      // console.debug('payment info not valid');
      return;
    }

    // ready to submit.  Copy form data.
    const data = this.form.getRawValue();
    // console.debug(JSON.stringify(data));
    const picked = pickBy(data, (value, key) => { return key.startsWith('billing'); });
    Object.assign(this.payment, picked);
    if (data.poNumber && data.poNumber !== '') {
      this.payment.poNumber = data.poNumber;
    }

    const pi = this.paymentInfo.getSelectedPaymentMethod(this.payment);
    if (!pi) {
      this.log.error('cannot submit form without payment type');
    }
    else {
      this.observe(this.paymentService.submitPayment(this.payment, pi[1], pi[0], this.applyCredit ? this.creditToBeApplied : 0),
        {
          onSuccess: (rsp) => {
            let firstTimePurchase = 'false';
            if (rsp.meta.orderCount === rsp.payment.orders?.length) {
              firstTimePurchase = 'true';

            }
            this.analyticsService.emitPaymentForMultiOrderPayment(firstTimePurchase, rsp.payment);

            const availableCredit = this.applyCredit ? (this.account?.unappliedCredits ?? 0) - this.creditToBeApplied : this.account?.unappliedCredits;
            // console.debug('launching thank you with ' + availableCredit + ' from: ' + this.account?.unappliedCredits + ' ' + this.creditToBeApplied );

            const extras: NavigationExtras = {
              state: {
                payment: rsp.payment,
                meta: rsp.meta,
                availableCredit
              }
            };

            this.router.navigate(['dashboard', { outlets: { 'navbar-tab': ['thank-you'] } }], extras);
            return false;
          },
          onFailure: (msg) => {
            if (msg.messages && msg.messages.length > 0 && msg.messages[0].data?.accountAvailableCredit !== undefined && this.account) {
              // we received a response message with an updated unapplied credits amount.
              this.account.unappliedCredits = msg.messages[0].data?.accountAvailableCredit;
              let applyCredit = true;
              if (this.account.unappliedCredits === 0) {
                // if there is no credit left (value is zero), turn off the selection of credit.
                applyCredit = false;
              }

              this.checkoutState.next({
                credit: this.account.unappliedCredits,
                applyCredit
              });
            }
            return true;
          }
        });
    }
  }

  public reviewUpdate(anyAutoRenew?: boolean, anyMonthlyBilling?: boolean): void {
    // console.debug('got event ' + JSON.stringify(e));
    const confirmAutoRenew = this.form.controls['confirmAutoRenew'];
    const confirmMonthlyBilling = this.form.controls['confirmMonthlyBilling'];

    if (anyAutoRenew === false) {
      confirmAutoRenew.setValidators([]);
      confirmAutoRenew.disable();
      confirmAutoRenew.setValue(false);
      confirmAutoRenew.updateValueAndValidity();
    }
    else if (anyAutoRenew === true) {
      confirmAutoRenew.setValidators(Validators.requiredTrue);
      confirmAutoRenew.enable();
      confirmAutoRenew.updateValueAndValidity();
    }

    if (anyMonthlyBilling === false) {
      confirmMonthlyBilling.setValidators([]);
      confirmMonthlyBilling.disable();
      confirmMonthlyBilling.setValue(false);
      confirmMonthlyBilling.updateValueAndValidity();
    }
    else if (anyMonthlyBilling === true) {
      confirmMonthlyBilling.setValidators(Validators.requiredTrue);
      confirmMonthlyBilling.enable();
      confirmMonthlyBilling.updateValueAndValidity();
    }
  }
}